/**
 * Workflow Create / Edit / Clone — Cancel dirty-gate (ACME-1050).
 *
 * ## Standing product rule (app-wide for this ticket)
 * Unsaved / leave confirmations must NOT open when nothing changed.
 * No change → leave (or close) silently. Prompt only when there is real user change.
 * Applies to Workflows, Templates, step wizards, Users, etc. — every unsaved dialog
 * we migrate. Destructive confirms (Delete) always prompt; they are not “unsaved.”
 *
 * **Save button:** when a form uses this baseline (Edit / Clone Workflow), Save should
 * stay disabled until `hasUnsavedChanges()` / `isWorkflowCancelDirty` is true — nothing
 * to persist means Save stays off. Create still uses tab/validity `workFlowDisable`.
 *
 * ## Why this util exists (do not “simplify” away)
 * Child tabs (`workflow-general-info`, file-pattern, notification, steps, scheduler)
 * dispatch CustomEvents from `render()` / first mount. You MUST NOT set `isDirty = true`
 * from those handlers — opening a page or switching tabs would false-positive and
 * reintroduce the useless “Unsaved Changes” prompt on clean Cancel.
 *
 * Correct pattern:
 * 1. Capture `captureWorkflowCancelBaseline(...)` before the first await the user can type through.
 * 2. After General Info writes its own defaults (Business Admin BU auto-fill), fold only
 *    those fields with `applyWorkflowCancelBaselineDefaults` — do not recapture live form state.
 * 3. On Cancel, if `!isWorkflowCancelDirty(baseline, current)` → navigate with no dialog.
 * 4. Otherwise `openJhftDialog({ variant: "warning", ... })`.
 *
 * Call sites: `CreateWorkflowTabView`, `EditWorkflowTabView`, `CloneWorkflowTabView`.
 * Capture timing: Create in `connectedCallback` (form is typable while General Info loads);
 * Edit/Clone immediately after `apiLoading = false` (hydrate done, spinner gone). Then await
 * `afterGeneralInfoLoad` and fold `autoFilledBusinessUnitParticipant` only — never live form
 * BU. Create/Edit/Clone also fold from Cancel once lookups are done. GI is only in the DOM
 * on the General Information tab — `recordGeneralInfoAutoFill` keeps the load-written value
 * after the user switches tabs so Cancel does not need a mounted instance.
 *
 * ## Safe changes
 * - Extend normalize* fields when a new user-editable field should count as dirty.
 * - Keep ignoring UI/validation-only flags (`valid`, `isEdit`, Rate/Cron type widgets).
 *
 * ## Unsafe changes
 * - Latching dirty from `@onWorkflowNameChange` / `@onSchedulerChange` / etc.
 * - Recapturing live form state after an await (absorbs typing during load).
 * - Leaving the baseline null across `afterGeneralInfoLoad` / settle.
 * - Always calling `openJhftDialog` on Cancel without the dirty check.
 */

export type WorkflowCancelSnapshotInput = {
  workflowData?: Record<string, unknown> | null;
  filepattern?: Record<string, unknown> | null;
  notification?: unknown;
  steps?: unknown;
  schedulerData?: Record<string, unknown> | null;
};

function str(v: unknown): string {
  if (v == null) return "";
  return String(v).trim();
}

function boolish(v: unknown, defaultTrue = true): boolean {
  if (v === undefined || v === null || v === "") return defaultTrue;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v.toLowerCase() !== "false";
  return Boolean(v);
}

function asArray(v: unknown): unknown[] {
  return Array.isArray(v) ? v : [];
}

const STEP_SECRET_KEYS = new Set([
  "Password",
  "privateKey",
  "PGPKey",
  "ConnectionString",
  "SshPrivateKeyReference",
  "sshPrivateKeyReference",
]);

/**
 * Equality token for secrets. Same ceiling as workflow-step-popup-dirty:
 * ponytail: djb2 token, not a KDF. Collision = false-clean Cancel; upgrade to
 * SHA-256 if this snapshot is ever persisted or compared across sessions.
 */
function fingerprint(v: string): string {
  let h = 5381;
  for (let i = 0; i < v.length; i++) h = Math.imul(h, 33) ^ v.charCodeAt(i);
  return `#${h >>> 0}`;
}

function normalizeFileRename(fileRename: unknown): Record<string, string> {
  if (!fileRename || typeof fileRename !== "object") return {};
  const src = fileRename as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const k of Object.keys(src).sort()) {
    out[str(k)] = str(src[k]);
  }
  return out;
}

function normalizeSecretValue(sv: unknown): Record<string, string> {
  if (!sv || typeof sv !== "object") return {};
  const src = sv as Record<string, unknown>;
  const out: Record<string, string> = {};
  for (const k of Object.keys(src).sort()) {
    const raw = str(src[k]);
    out[k] = STEP_SECRET_KEYS.has(k) ? fingerprint(raw) : raw;
  }
  return out;
}

function normalizePatterns(filepattern: Record<string, unknown> | null | undefined) {
  const fp = filepattern ?? {};
  return {
    data: asArray(fp.DataFilePattern).map(str).slice().sort(),
    trigger: asArray(fp.TriggerFilePattern).map(str).slice().sort(),
    // Typed but not yet added via "+". file-pattern-view writes these onto the
    // shared formData object on @input so Cancel sees in-progress text.
    pendingData: str(fp.dataFileName),
    pendingTrigger: str(fp.triggerFileName),
  };
}

function normalizeNotifications(notification: unknown) {
  return asArray(notification)
    .map((n: any) => ({
      messageType: str(n?.messageType),
      notificationType: str(n?.notificationType),
      to: str(n?.to),
      subject: str(n?.subject),
      template: str(n?.template),
    }))
    .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
}

function normalizeSteps(steps: unknown) {
  return asArray(steps)
    .map((s: any) => ({
      stepIndex: Number(s?.stepIndex) || 0,
      stepName: str(s?.stepName),
      functionName: str(s?.functionName),
      deleteSourceFile: boolish(s?.deleteSourceFile, false),
      path: str(s?.path),
      name: str(s?.name),
      storageType: str(s?.storageType),
      FileMovePattern: asArray(s?.FileMovePattern).map(str).slice().sort(),
      institutionUuid: str(s?.institutionUuid),
      locationType: str(s?.locationType),
      HostName: str(s?.HostName),
      Port: str(s?.Port),
      UserName: str(s?.UserName),
      AuthType: str(s?.AuthType),
      bucketRename: str(s?.bucketRename),
      secretName: str(s?.secretName),
      fileRename: normalizeFileRename(s?.fileRename),
      Password: fingerprint(str(s?.Password)),
      privateKey: fingerprint(str(s?.privateKey)),
      PGPKey: fingerprint(str(s?.PGPKey)),
      ConnectionString: fingerprint(str(s?.ConnectionString)),
      RoleARN: str(s?.RoleARN),
      Region: str(s?.Region),
      secretValue: normalizeSecretValue(s?.secretValue),
    }))
    .sort((a, b) => a.stepIndex - b.stepIndex);
}

/** Scheduler UI expands Expression into Type/Rate/Cron — compare persisted fields only. */
function normalizeScheduler(schedulerData: Record<string, unknown> | null | undefined) {
  const s = schedulerData ?? {};
  return {
    Expression: str(s.Expression),
    Description: str(s.Description),
    TimeZone: str(s.TimeZone),
    StartDate: str(s.StartDate),
    StartHour: str(s.StartHour),
    StartMinute: str(s.StartMinute),
    EndDate: str(s.EndDate),
    EndHour: str(s.EndHour),
    EndMinute: str(s.EndMinute),
  };
}

/**
 * Stable JSON of user-meaningful form state for Cancel compare.
 * Omits validation / ephemeral UI flags on purpose.
 */
export function captureWorkflowCancelBaseline(input: WorkflowCancelSnapshotInput): string {
  const wd = input.workflowData ?? {};
  const snapshot = {
    WorkflowName: str(wd.WorkflowName),
    TransferType: str(wd.TransferType).toLowerCase(),
    BusinessUnitParticipant: str(wd.BusinessUnitParticipant).toLowerCase(),
    sourceLocationPath: str(wd.sourceLocationPath),
    DeleteFiles: boolish(wd.DeleteFiles, true),
    patterns: normalizePatterns(input.filepattern),
    notification: normalizeNotifications(input.notification),
    steps: normalizeSteps(input.steps),
    scheduler: normalizeScheduler(input.schedulerData),
  };
  return JSON.stringify(snapshot);
}

/**
 * Fold values the load wrote itself into an existing baseline, so they do not read as
 * user changes while everything the user typed in the meantime still does.
 *
 * Only keys present on `defaults` are patched. Passing `{ workflowData: { BusinessUnitParticipant } }`
 * after General Info auto-fill is the Create/Clone case — do not pass the whole live form.
 */
export function applyWorkflowCancelBaselineDefaults(
  baseline: string | null | undefined,
  defaults: WorkflowCancelSnapshotInput,
): string {
  let captured: Record<string, unknown>;
  try {
    captured = JSON.parse(
      baseline || captureWorkflowCancelBaseline({}),
    ) as Record<string, unknown>;
  } catch {
    captured = JSON.parse(captureWorkflowCancelBaseline({})) as Record<string, unknown>;
  }
  const wd = defaults.workflowData;
  if (wd && typeof wd === "object") {
    if ("WorkflowName" in wd) captured.WorkflowName = str(wd.WorkflowName);
    if ("TransferType" in wd) captured.TransferType = str(wd.TransferType).toLowerCase();
    if ("BusinessUnitParticipant" in wd) {
      captured.BusinessUnitParticipant = str(wd.BusinessUnitParticipant).toLowerCase();
    }
    if ("sourceLocationPath" in wd) captured.sourceLocationPath = str(wd.sourceLocationPath);
    if ("DeleteFiles" in wd) captured.DeleteFiles = boolish(wd.DeleteFiles, true);
  }
  if (defaults.filepattern != null) captured.patterns = normalizePatterns(defaults.filepattern);
  if (defaults.notification !== undefined) {
    captured.notification = normalizeNotifications(defaults.notification);
  }
  if (defaults.steps !== undefined) captured.steps = normalizeSteps(defaults.steps);
  if (defaults.schedulerData != null) captured.scheduler = normalizeScheduler(defaults.schedulerData);
  return JSON.stringify(captured);
}

/** @returns false when baseline missing (treat as clean — leave silently). */
export function isWorkflowCancelDirty(
  baseline: string | null | undefined,
  input: WorkflowCancelSnapshotInput,
): boolean {
  if (!baseline) return false;
  return captureWorkflowCancelBaseline(input) !== baseline;
}

type GiAutoFillRecord = {
  lookupsDone: boolean;
  autoFilled: string | undefined;
};

const giAutoFillByForm = new WeakMap<object, GiAutoFillRecord>();

/**
 * GI calls this when loadLookups finishes so parents can fold after the tab unmounts.
 * Keyed by the shared `formData` / `workflowData` object (same reference parents pass in).
 */
export function recordGeneralInfoAutoFill(
  formData: object | null | undefined,
  autoFilled: string | undefined,
): void {
  if (!formData || typeof formData !== "object") return;
  giAutoFillByForm.set(formData, { lookupsDone: true, autoFilled });
}

/**
 * Prefer a mounted GI instance; if the user left General Info, use the record
 * loadLookups left on the shared formData object. Never invent a value from live BU.
 */
export function resolveGeneralInfoAutoFill(
  formData: object | null | undefined,
  gi?: { lookupsDone?: boolean; autoFilledBusinessUnitParticipant?: string } | null,
): GiAutoFillRecord {
  if (gi?.lookupsDone) {
    return { lookupsDone: true, autoFilled: gi.autoFilledBusinessUnitParticipant };
  }
  if (formData && typeof formData === "object") {
    const rec = giAutoFillByForm.get(formData);
    if (rec) return rec;
  }
  return { lookupsDone: false, autoFilled: undefined };
}

/**
 * Wait for `workflow-general-info` lookups (and Business Admin BU auto-fill) to land.
 *
 * After this resolves, fold `BusinessUnitParticipant` into an existing baseline with
 * `applyWorkflowCancelBaselineDefaults`. Do not recapture live form state.
 * No-ops when the tab is not rendered (General Info is not the active tab) — Cancel
 * still folds via `recordGeneralInfoAutoFill` once lookups finish on the unmounted GI.
 */
export async function afterGeneralInfoLoad(host: Element | null | undefined): Promise<void> {
  const generalInfo = host?.shadowRoot?.querySelector("workflow-general-info") as
    | { loadComplete?: Promise<void> }
    | null
    | undefined;
  await generalInfo?.loadComplete;
}

/**
 * Two animation frames so child tabs can mount and fire initial render events
 * before we fold — otherwise the first BU autofill / steps dispatch looks “dirty.”
 * A timeout fallback covers hidden tabs where rAF is paused.
 */
export async function afterWorkflowFormSettle(timeoutMs = 250): Promise<void> {
  await new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    const t = setTimeout(finish, timeoutMs);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        clearTimeout(t);
        finish();
      });
    });
  });
}
